import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const StrategicIntelligenceAlerts = ({ strategicAlerts, selectedMineral = 'Lithium', geopoliticalAnalysis, matrixData }) => {
  const { isDarkMode } = useTheme();
  
  // Use only real data from API
  const data = strategicAlerts || {};
  
  // Use geopolitical analysis data if provided
  const geoData = geopoliticalAnalysis || { groups: [] };
  
  const diversificationValue = data.diversificationIndex || 0;
  const allianceValue = data.allianceDependency || 0;
  // const rivalExposureValue = data.rivalExposure || 0;

  // Calculate total percentage for only BRICS, G20, and QUAD groups
  const filteredGroups = geoData.groups?.filter(group => 
    ['BRICS', 'G20', 'QUAD'].includes(group.label)
  ) || [];
  
  
  // Ensure BRICS, G20, and QUAD are always shown, even with 0% dependency
  const requiredGroups = ['BRICS', 'G20', 'QUAD'];
  const finalGroups = requiredGroups.map(label => {
    const existingGroup = filteredGroups.find(g => g.label === label);
    if (existingGroup) {
      return existingGroup;
    } else {
      // Show group with 0% if not found in data
      return {
        id: label.toLowerCase(),
        label: label,
        importDependencyPercentage: 0,
        color: label === 'BRICS' ? '#ef4444' : label === 'G20' ? '#3b82f6' : '#10b981'
      };
    }
  });
  
  // Calculate total from the displayed values to ensure consistency
  const filteredAllianceValue = finalGroups.reduce((sum, group) => {
    const value = group.importDependencyPercentage || 0;
    // Use the same rounding as the display (1 decimal place)
    const roundedValue = Math.round(value * 10) / 10;
    return sum + roundedValue;
  }, 0);

  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 h-full transition-colors duration-200">
      <div className="mb-3">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
          Strategic Intelligence Alerts
        </h3>
      </div>

      {/* Main Alert Cards - Compact Layout */}
      <div className="space-y-3">
        {/* Diversification Index */}
        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 transition-colors duration-200">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-xs font-medium text-gray-600 dark:text-gray-300">Diversification Index</h4>
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white">
            {diversificationValue.toFixed(2)}
          </div>
        </div>

        {/* Alliance Dependency */}
        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 transition-colors duration-200">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-xs font-medium text-gray-600 dark:text-gray-300">Strategic Grouping Dependency</h4>
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {filteredAllianceValue.toFixed(2)}%
          </div>
          
          {/* Geopolitical Group Breakdown - Only BRICS, G20, and QUAD */}
          <div className="space-y-1 text-xs">
            {finalGroups.map((group) => {
              const groupDependency = group.importDependencyPercentage || 0;
              const groupColor = group.color || '#6b7280';
              
              return (
                <div key={group.id} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div 
                      className="w-2 h-2 rounded-full mr-2"
                      style={{ backgroundColor: groupColor }}
                    ></div>
                    <span className="text-gray-600 dark:text-gray-300">{group.label}:</span>
                  </div>
                  <span className="text-gray-900 dark:text-white font-medium">
                    {groupDependency.toFixed(1)}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Rival Exposure
        <div className="bg-gray-700 rounded-lg p-3">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-xs font-medium text-gray-300">Rival Exposure</h4>
          </div>
          <div className="text-xl font-bold text-white">
            {rivalExposureValue.toFixed(2)}%
          </div>
        </div> */}

        {/* Top Opportunity */}
        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 transition-colors duration-200">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-xs font-medium text-gray-600 dark:text-gray-300">Top Opportunity</h4>
          </div>
          <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
            {data.topOpportunity?.country || ''}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {data.topOpportunity?.untappedOpportunity ? 
              `${data.topOpportunity.untappedOpportunity.toFixed(1)}% untapped potential` : 
              ''
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrategicIntelligenceAlerts;
